import {
  ApiError,
  backendFetchResponse,
  parseResponse,
} from "@softmax/observatory-client/transport";
function canonicalPath(path: string): string {
  const queryStart = path.indexOf("?");
  const route = queryStart === -1 ? path : path.slice(0, queryStart);
  const search = path.split("?", 2)[1];
  const parameters = new URLSearchParams(search);

  parameters.sort();
  return parameters.size ? `${route}?${parameters}` : route;
}
export function createPlayTransport({
  signal,
}: { readonly signal?: AbortSignal | undefined } = {}) {
  const reads = new Map<string, Promise<Response>>();
  function read({
    path,
    anonymous = false,
  }: {
    readonly path: string;
    readonly anonymous?: boolean;
  }): Promise<Response> {
    const canonical = canonicalPath(path);
    const key = `${anonymous ? "public" : "viewer"}:${canonical}`;
    let pending = reads.get(key);

    if (!pending) {
      pending = backendFetchResponse("/api/observatory", canonical, {
        headers: { "X-Use-Elevated-Privileges": "false" },
        credentials: anonymous ? "omit" : "same-origin",
        signal: signal ?? null,
      });
      reads.set(key, pending);
    }

    return pending;
  }
  async function playFetchWithHeaders<T>({
    path,
    anonymous = false,
  }: {
    readonly path: string;
    readonly anonymous?: boolean;
  }): Promise<{
    body: T;
    headers: Headers;
  } | null> {
    const response = await read({
      path,
      anonymous: anonymous,
    });

    if (response.status === 401 || response.status === 403)
      throw new ApiError("This play resource requires access", response.status);

    if (!response.ok) {
      console.error(`[play] GET ${path} -> ${response.status}`);
      return null;
    }

    return {
      body: await parseResponse<T>(response.clone()),
      headers: response.headers,
    };
  }
  const playFetch = async <T>({
    path,
    anonymous = false,
  }: {
    readonly path: string;
    readonly anonymous?: boolean;
  }): Promise<T | null> => {
    return (
      (
        await playFetchWithHeaders<T>({
          path,
          anonymous,
        })
      )?.body ?? null
    );
  };
  const get = async <T>({
    path,
    anonymous = false,
  }: {
    readonly path: string;
    readonly anonymous?: boolean;
  }): Promise<T> => {
    const response = await read({
      path,
      anonymous: anonymous,
    });

    if (!response.ok)
      throw new ApiError(
        `Play resource unavailable (${response.status})`,
        response.status,
      );

    return parseResponse<T>(response.clone());
  };
  return { playFetchWithHeaders, playFetch, get };
}

export type PlayTransport = ReturnType<typeof createPlayTransport>;
